import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, FormStore, inject, Input, Loader, Modal, ModalHandle, TasksStore } from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Collapse, Form, FormGroup } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { DisplayGroupsStore } from './DisplayGroupsStore';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';

export interface IDisplayGroupRowProps {
    readonly displayGroup: Mri.DisplayGroups.DisplayGroup;
    readonly hideBorder?: boolean;
}

@autoinject
@observer
export class DisplayGroupItem extends React.Component<IDisplayGroupRowProps> {
    public render() {
        const location = `/display-groups`;

        return (
            <div className={this.props.hideBorder ? 'card d-block h-100 border-0' : 'card d-block h-100'}>
                {this.displayGroup && (
                    <div className="card-body p-1 p-md-2">
                        <div className="w-100 d-flex">
                            <p className="mb-0 d-inline-block fw-bold">{this.displayGroup?.name} </p>

                            <a className="btn text-danger py-0 ms-auto" onClick={this.delete}>
                                <BsTrash className="" />
                            </a>

                            <LinkContainer
                                exact
                                to={location + `/${this.displayGroup.name}`}
                                className="btn btn-link fw-bold p-0"
                            >
                                <a>
                                    <BsPencilSquare />
                                </a>
                            </LinkContainer>
                        </div>
                        <small className="d-block">{this.displayGroup.description}</small>

                        {this.displayGroup.displayEventProfiles &&
                            this.displayGroup.displayEventProfiles.length > 0 && (
                            <small className="d-block">
                                    Event Profiles: {this.displayGroup.displayEventProfiles.length}
                            </small>
                        )}

                        {this.displayGroup.softwareUpdateManifests &&
                            this.displayGroup.softwareUpdateManifests.length > 0 && (
                            <small className="d-block">
                                    Manifests: {this.displayGroup.softwareUpdateManifests.length}
                            </small>
                        )}

                        <div className="d-flex w-100 justify-content-between">
                            <a onClick={this.openDetailsModal} className="btn btn-link p-0">
                                <p className="mb-0">Details</p>
                            </a>
                        </div>
                    </div>
                )}

                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">{'Delete ' + this.props.displayGroup.name + '?'}</div>
                </Modal>

                <Modal
                    className="modal-sm"
                    title={this.props.displayGroup.name}
                    modalHandle={this.detailsModalHandle}
                    btnClass="w-50"
                    footerClass="d-none"
                >
                    {this.displayGroup && (
                        <div className="col-12 px-0 pt-1">
                            <Form>
                                <FormGroup color={this.editGroupForm.fields.description.state}>
                                    <label>Description</label>

                                    <Input
                                        type="text"
                                        field={this.editGroupForm.fields.description}
                                        placeholder="Description"
                                        required
                                    />
                                </FormGroup>

                                {this.displayGroup.displayEventProfiles &&
                                    this.displayGroup.displayEventProfiles.length > 0 && (
                                    <>
                                        <label>Event Profiles</label>

                                        <div
                                            className={
                                                this.displayGroup.displayEventProfiles.length > 4
                                                    ? 'scroll-container w-100 minh-200 mb-3'
                                                    : ''
                                            }
                                        >
                                            <div
                                                className={
                                                    this.displayGroup.displayEventProfiles.length > 4
                                                        ? 'scroll-card'
                                                        : ''
                                                }
                                            >
                                                <ul>
                                                    {this.displayGroup.displayEventProfiles.map((p, k) => (
                                                        <li key={k}>
                                                            <LinkContainer
                                                                to={`/display-event-profiles/${
                                                                    p.displayEventStatus != null
                                                                        ? 'automations'
                                                                        : 'alerts'
                                                                }/${p.displayEventProfileGuid}/edit`}
                                                            >
                                                                <a>{p.name}</a>
                                                            </LinkContainer>

                                                            {p.comment && (
                                                                <div className="ellipsis">- {p.comment}</div>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {this.displayGroup.softwareUpdateManifests &&
                                    this.displayGroup.softwareUpdateManifests.length > 0 && (
                                    <>
                                        <label>Manifests</label>

                                        <div
                                            className={
                                                this.displayGroup.softwareUpdateManifests.length > 8
                                                    ? 'scroll-container w-100 minh-200 mb-3'
                                                    : ''
                                            }
                                        >
                                            <div
                                                className={
                                                    this.displayGroup.softwareUpdateManifests.length > 8
                                                        ? 'scroll-card'
                                                        : ''
                                                }
                                            >
                                                <ul>
                                                    {this.displayGroup.softwareUpdateManifests.map((m, k) => (
                                                        <li key={k}>
                                                            <LinkContainer
                                                                to={`/software-updates/manifests/${m.softwareUpdateManifestGuid}/status`}
                                                            >
                                                                <a>{m.name}</a>
                                                            </LinkContainer>

                                                            {m.version > 0 && (
                                                                <span className="mx-2">Version: {m.version}</span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <FormGroup>
                                    <a className="btn btn-primary" onClick={this.submitAsync}>
                                        <Loader loading={this.loading} showSpinner message="Updating..." />
                                        {!this.loading && 'Update'}
                                    </a>
                                </FormGroup>
                            </Form>
                        </div>
                    )}
                </Modal>
            </div>
        );
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLAnchorElement>) {
        event.preventDefault();

        this.editGroupForm.onSubmit();
        if (this.editGroupForm.validate()) {
            const name = this.editGroupForm.values.name;
            this.loading = true;
            const task = this.tasksStore.startSessionTask(`Update Display Group ${name}`);

            try {
                await this.displayGroupsStore.updateAsync(this.props.displayGroup.name, this.editGroupForm.values);
                task.complete();
            } catch (e: any) {
                if (e.modelState) {
                    this.editGroupForm.clearGlobalErrors();
                    this.editGroupForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.detailsModalHandle.close();
                this.loading = false;
            }
        } else {
            console.error('The Form is invalid');
        }
    }

    @bind
    private async delete(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        if (await this.modalHandle.confirm()) {
            const task = this.tasksStore.startSessionTask(`Delete Display Group ${this.props.displayGroup.name}`);

            try {
                this.displayGroupsStore.deleteAsync(this.props.displayGroup.name);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @bind
    @action
    private async openDetailsModal() {
        this.detailsModalHandle.open();
    }

    @computed
    private get displayGroup() {
        return this.displayGroupsStore.displayGroups.get(this.props.displayGroup.name);
    }

    @computed
    private get editGroupForm() {
        return new FormStore<Mri.DisplayGroups.DisplayGroup>(this.props.displayGroup);
    }

    @observable
    private loading: boolean = false;
    @observable
    private collapsed = true;
    @observable
    private modalHandle = new ModalHandle();
    @observable
    private detailsModalHandle = new ModalHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private tasksStore!: TasksStore;
    @inject
    private displayGroupsStore!: DisplayGroupsStore;
}
